import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { PMTiles } from 'pmtiles'
import { Capacitor } from '@capacitor/core'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { landOwnershipService } from '../land/land-ownership-service.js'

const log = logger.create('critical-habitat')

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const PMTILES_URL = `${TILE_ORIGIN}/data/wildlife/critical-habitat.pmtiles`

const SOURCE_ID = 'critical-habitat-source'
const FILL_LAYER = 'critical-habitat-fill'
const LINE_LAYER = 'critical-habitat-outline'

const DEFAULT_OPACITY = 0.25
const MIN_ZOOM = 3

const STATUS_COLORS: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'status'],
  'Endangered',
  '#dc2626',
  'Threatened',
  '#ea580c',
  '#a855f7',
]

class CriticalHabitatService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY

  get enabled(): boolean {
    return this._enabled
  }

  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map): void {
    this.map = map
    const protocol = landOwnershipService.getProtocol()
    if (protocol) {
      protocol.add(new PMTiles(PMTILES_URL))
    } else {
      log.warn('PMTiles protocol not available — critical habitat tiles will not load')
    }
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'vector',
        url: `pmtiles://${PMTILES_URL}`,
        minzoom: MIN_ZOOM,
        maxzoom: 14,
      })
    }

    if (!this.map.getLayer(FILL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: FILL_LAYER,
          type: 'fill',
          source: SOURCE_ID,
          'source-layer': 'critical_habitat',
          minzoom: MIN_ZOOM,
          paint: {
            'fill-color': STATUS_COLORS,
            'fill-opacity': this._opacity,
          },
        },
        'wildlife-critical-habitat'
      )
    }

    if (!this.map.getLayer(LINE_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: LINE_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'critical_habitat',
          minzoom: MIN_ZOOM,
          paint: {
            'line-color': STATUS_COLORS,
            'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.3, 10, 0.8, 14, 1.5],
            'line-opacity': Math.min(this._opacity + 0.2, 1),
          },
        },
        'wildlife-critical-habitat'
      )
    }
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    if (this.map.getLayer(LINE_LAYER)) this.map.removeLayer(LINE_LAYER)
    if (this.map.getLayer(FILL_LAYER)) this.map.removeLayer(FILL_LAYER)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.enable()
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(FILL_LAYER)) {
      this.map.setPaintProperty(FILL_LAYER, 'fill-opacity', this._opacity)
    }
    if (this.map?.getLayer(LINE_LAYER)) {
      this.map.setPaintProperty(LINE_LAYER, 'line-opacity', Math.min(this._opacity + 0.2, 1))
    }
  }

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map || !this._enabled) return []
    if (!this.map.getLayer(FILL_LAYER)) return []
    return this.map.queryRenderedFeatures(point, { layers: [FILL_LAYER] })
  }
}

export const criticalHabitatService = new CriticalHabitatService()
